import { useConfig, useNotification, useOidcWithAdmin } from '@revenuewell/front-end-bundle';
import { Contact, ContactApi } from 'apps/wrapper/src/services/api-client/contact-api';
import AppointmentService from 'apps/wrapper/src/services/appointment/appointment-service';
import { AppointmentRequestDetails } from 'apps/wrapper/src/services/appointment/types/appointment-request-details';
import { AppointmentRequestStatus } from 'apps/wrapper/src/services/appointment/types/appointment-request-status';
import { CancellationAppointment } from 'apps/wrapper/src/services/appointment/types/cancellation-appointment';
import { FC, ReactNode, useEffect, useMemo, useState } from 'react';
import { useApptRequestsWidget } from '../use-appt-requests-widget';
import { CancellationRequestViewContext } from './context';

export interface CancellationRequestViewProviderProps {
  apptRequestId: number;
  children: ReactNode;
}

export const CancellationRequestViewProvider: FC<CancellationRequestViewProviderProps> = (props) => {
  const { apptRequestId } = props;
  const { config } = useConfig();
  const { showError } = useNotification();
  const { oidcService } = useOidcWithAdmin();
  const { locationId } = useApptRequestsWidget();
  const { goBack } = useApptRequestsWidget();
  const [isLoading, setIsLoading] = useState(true);
  const [contact, setContact] = useState<Contact>();
  const [requestDetails, setRequestDetails] = useState<AppointmentRequestDetails>(null!);
  const [appointment, setAppointment] = useState<CancellationAppointment>();
  const appointmentService = useMemo(() => new AppointmentService(config, oidcService), [config, oidcService]);
  const contactApi = ContactApi.getInstance();

  const isEditable = useMemo(() => {
    if (!requestDetails) return false;
    return (
      requestDetails.status === AppointmentRequestStatus.New ||
      requestDetails.status === AppointmentRequestStatus.Pending
    );
  }, [requestDetails]);

  useEffect(() => {
    const init = async () => {
      setIsLoading(true);

      const [apptRequestDetails, cancellationAppointment] = await Promise.all([
        loadApptRequestDetails(apptRequestId, locationId),
        loadCancellationAppointment(apptRequestId, locationId)
      ]);

      if (!apptRequestDetails || !cancellationAppointment) {
        goBack();
        showError('Unable to load appointment request details');

        return;
      }

      if (apptRequestDetails.patient.id) {
        const contactDetails = await contactApi.getContactDetailsById(apptRequestDetails.patient.id);

        setContact(contactDetails);
      }

      setAppointment(cancellationAppointment);
      setRequestDetails(apptRequestDetails);
      setIsLoading(false);
    };

    init();
  }, [apptRequestId, locationId]);

  const loadApptRequestDetails = async (apptReqId: number, locId: number) => {
    try {
      return await appointmentService.getAppointmentRequestDetails(apptReqId, locId);
    } catch {
      showError('Unable to load appointment request details');

      return null;
    }
  };

  const loadCancellationAppointment = async (apptReqId: number, locId: number) => {
    try {
      return await appointmentService.getCancellationAppointment(apptReqId, locId);
    } catch {
      showError('Unable to load appointment details');

      return null;
    }
  };

  const contextValue = useMemo(
    () => ({
      isLoading,
      contact,
      isEditable,
      requestDetails,
      appointment
    }),
    [isLoading, contact, isEditable, requestDetails, appointment]
  );

  return (
    <CancellationRequestViewContext.Provider value={contextValue}>
      {props.children}
    </CancellationRequestViewContext.Provider>
  );
};
